/**
 * Containment — where a thing IS when it is inside something else (D50),
 * and, since D240, when that something is a BODY.
 *
 * @remarks
 * Moved out of `items.ts` at the 500-line cap when D240 gave containment its
 * second shape. Nothing changed for a thing inside a thing: `items.ts`
 * re-exports both names, so every import of them is unmoved.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { type ActorId, actorIdSchema, type ItemId, itemIdSchema } from './identifiers.js';

/** Hidden until the container is SEARCHED, not merely opened — the one field both shapes share. */
const concealedSchema = z.literal(true).optional();

/**
 * D50: a thing inside another THING — the locker's keycard, the slate's
 * false back.
 *
 * @remarks
 * Authored on the CONTAINED item rather than on the container — Calder's ask,
 * and the stronger model for a reason neither of us named at first: one field
 * per item makes "an item is in exactly one container" true by CONSTRUCTION.
 * The container-side draft needed a cross-check to catch two lockers claiming
 * one keycard; this cannot express the mistake, so the amendment deleted a
 * validation rule rather than adding one.
 *
 * `concealed` is what makes `search` a distinct verb from `look`: looking shows
 * what is plainly there, searching reveals what is not. A thing can be inside
 * an OPEN container and still concealed — the sheaf behind the pipe — which is
 * exactly the case the flag-as-visibility trick could never express.
 *
 * D239: containment NESTS, and a concealed thing with its own `openable` is a
 * compartment — found by searching what hides it, opened only on its own
 * door's terms (`handOpenCondition`: a tool in the hand, a body in the room,
 * power), and holding things of its own, found in turn by searching it. The
 * container may be carried: what a search of the slate in hand finds stays in
 * the slate until it is taken. `item_found` asks after the concealed thing.
 */
export const itemContainedInSchema = z
	.strictObject({
		concealed: concealedSchema,
		containerItemId: itemIdSchema,
	})
	.readonly();

/**
 * D240: a thing a BODY carries — TALLY's sealed sample bay, a guard's
 * pocket (the Meridian's §6.9, the Act I specification's §13.15).
 *
 * @remarks
 * `actors.md` §2 recorded "an actor's pockets are fiction until a pack needs
 * them", and the Meridian needs one. The shape is D50's with the container
 * named as an actor, so everything a thing inside a thing already does
 * carries over unchanged: `concealed` waits for `search <the body>`, the
 * found fact is `item_found`, the find speaks its `foundNarratives`, and a
 * found thing that declares `openable` is a compartment on its own door's
 * terms — the bay stays sealed until a tool, a flag or power opens it — and
 * may hold things of its own (their `containerItemId` naming the bay).
 *
 * What the body adds is LOCATION. A body's contents are where the body is:
 * referable while it is PRESENT (in the player's room and visible — a voice
 * heard through steel lends the hands nothing), carried along by `follows`
 * and `move_actor` with no record of their own, and in the hands only once
 * the player takes them out. A body is never a container in the item sense —
 * it has no door; a compartment on it is an item that does.
 */
export const actorContainedInSchema = z
	.strictObject({
		concealed: concealedSchema,
		containerActorId: actorIdSchema,
	})
	.readonly();

/**
 * Where a contained thing lives: inside a thing (D50), or carried by a body
 * (D240). Exactly one container is named, by construction — a union of two
 * strict shapes, so naming both, or neither, is a schema error rather than a
 * loader rule.
 */
export const containedInSchema = z.union([itemContainedInSchema, actorContainedInSchema]);

/** Where a contained thing lives — see {@link containedInSchema}. */
export type ContainedIn = z.infer<typeof containedInSchema>;

/**
 * The THING a contained thing is inside (D240's reader for D50's shape).
 *
 * @param containedIn - The contained thing's `containedIn`, when it has one
 * @returns The container's item id; `undefined` for a body's contents, or for a thing contained by nothing
 */
export const containerItemIdOf = (containedIn: ContainedIn | undefined): ItemId | undefined =>
	containedIn !== undefined && 'containerItemId' in containedIn ? containedIn.containerItemId : undefined;

/**
 * The BODY a contained thing is carried by (D240).
 *
 * @param containedIn - The contained thing's `containedIn`, when it has one
 * @returns The carrying actor's id; `undefined` for a thing inside a thing, or for a thing contained by nothing
 */
export const containerActorIdOf = (containedIn: ContainedIn | undefined): ActorId | undefined =>
	containedIn !== undefined && 'containerActorId' in containedIn ? containedIn.containerActorId : undefined;
